import React, { useState } from 'react';

interface TableButtonProps {
  editor: any;
  disabled?: boolean;
}

const TableButton: React.FC<TableButtonProps> = ({ editor, disabled = false }) => {
  const [showTableDialog, setShowTableDialog] = useState(false);
  const [rows, setRows] = useState(3);
  const [cols, setCols] = useState(3);

  const insertTable = () => {
    if (!editor) return;
    
    editor.chain().focus().insertTable({ 
      rows: rows, 
      cols: cols, 
      withHeaderRow: true 
    }).run();
    
    setShowTableDialog(false);
    setRows(3);
    setCols(3);
  };

  const addColumnBefore = () => {
    if (!editor) return;
    editor.chain().focus().addColumnBefore().run();
  };

  const addColumnAfter = () => {
    if (!editor) return;
    editor.chain().focus().addColumnAfter().run();
  };

  const deleteColumn = () => {
    if (!editor) return;
    editor.chain().focus().deleteColumn().run();
  };

  const addRowBefore = () => {
    if (!editor) return;
    editor.chain().focus().addRowBefore().run();
  };

  const addRowAfter = () => {
    if (!editor) return;
    editor.chain().focus().addRowAfter().run();
  };

  const deleteRow = () => {
    if (!editor) return;
    editor.chain().focus().deleteRow().run();
  };

  const deleteTable = () => {
    if (!editor) return;
    editor.chain().focus().deleteTable().run();
  };

  const toggleHeaderColumn = () => {
    if (!editor) return;
    editor.chain().focus().toggleHeaderColumn().run();
  };

  const toggleHeaderRow = () => {
    if (!editor) return;
    editor.chain().focus().toggleHeaderRow().run();
  };

  const isInTable = editor?.isActive('table');

  return (
    <div className="relative">
      {/* Main Table Button */}
      <button
        onClick={() => setShowTableDialog(true)}
        disabled={disabled}
        className={`p-2 rounded hover:bg-gray-100 ${
          isInTable ? 'bg-gray-200' : ''
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        title="Insert Table"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0V4a1 1 0 011-1h16a1 1 0 011 1v16a1 1 0 01-1 1H4a1 1 0 01-1-1V10z" />
        </svg>
      </button>

      {/* Table Creation Dialog */}
      {showTableDialog && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-50 min-w-[200px]">
          <div className="space-y-3">
            <div className="text-sm font-medium text-gray-700">Insert Table</div>
            
            <div className="flex items-center space-x-2">
              <label className="text-xs text-gray-600">Rows:</label>
              <input
                type="number"
                min="1"
                max="20"
                value={rows}
                onChange={(e) => setRows(parseInt(e.target.value) || 1)}
                className="w-16 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <label className="text-xs text-gray-600">Cols:</label>
              <input
                type="number"
                min="1"
                max="20"
                value={cols}
                onChange={(e) => setCols(parseInt(e.target.value) || 1)}
                className="w-16 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            
            <div className="flex space-x-2 pt-2">
              <button
                onClick={insertTable}
                className="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
              >
                Insert
              </button>
              <button
                onClick={() => setShowTableDialog(false)}
                className="px-3 py-1 bg-gray-300 text-gray-700 rounded text-xs hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table Manipulation Buttons (shown when in table) */}
      {isInTable && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-2 z-50 flex space-x-1">
          <button
            onClick={addColumnBefore}
            className="p-1 rounded hover:bg-gray-100 text-xs"
            title="Add Column Before"
          >
            ←+
          </button>
          <button
            onClick={addColumnAfter}
            className="p-1 rounded hover:bg-gray-100 text-xs"
            title="Add Column After"
          >
            +→
          </button>
          <button
            onClick={deleteColumn}
            className="p-1 rounded hover:bg-gray-100 text-xs text-red-600"
            title="Delete Column"
          >
            ←×
          </button>
          <div className="w-px h-4 bg-gray-300 my-1" />
          <button
            onClick={addRowBefore}
            className="p-1 rounded hover:bg-gray-100 text-xs"
            title="Add Row Before"
          >
            ↑+
          </button>
          <button
            onClick={addRowAfter}
            className="p-1 rounded hover:bg-gray-100 text-xs"
            title="Add Row After"
          >
            +↓
          </button>
          <button
            onClick={deleteRow}
            className="p-1 rounded hover:bg-gray-100 text-xs text-red-600"
            title="Delete Row"
          >
            ↑×
          </button>
          <div className="w-px h-4 bg-gray-300 my-1" />
          <button
            onClick={toggleHeaderRow}
            className="p-1 rounded hover:bg-gray-100 text-xs"
            title="Toggle Header Row"
          >
            H↔
          </button>
          <button
            onClick={toggleHeaderColumn}
            className="p-1 rounded hover:bg-gray-100 text-xs"
            title="Toggle Header Column"
          >
            H↕
          </button>
          <button
            onClick={deleteTable}
            className="p-1 rounded hover:bg-gray-100 text-xs text-red-600"
            title="Delete Table"
          >
            ⊗
          </button>
        </div>
      )}
    </div>
  );
};

export default TableButton; 